import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { useConfig } from "../../contexts/ConfigContext";
import { useCsvData } from "../../api/csvData";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 100;

export interface CsvDataTableProps {
  /** dataset.original_file_key — the raw CSV S3 key the endpoint is keyed by. */
  fileKey: string | null | undefined;
  /** Ordered column names from stac_properties.column_schema (optional hint). */
  columns?: string[];
  /** Scroll height for the table body. */
  maxHeight?: number | string;
  /** Gate the query (e.g. only when the containing dialog/viewer is visible). */
  enabled?: boolean;
}

function renderCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Reusable, read-only paginated table of a tabular dataset's CSV rows, fetched from
 * the config-driven `/api/v2/csv-data/` endpoint. Self-contained (owns page state) so
 * it can be embedded in a dialog, a MediaViewer renderer, or any panel.
 */
export default function CsvDataTable({ fileKey, columns: columnsHint, maxHeight = "60vh", enabled = true }: CsvDataTableProps) {
  const config = useConfig();
  const endpoint = config.tabularData?.csvData ?? "/api/v2/csv-data/";

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading, isError, isFetching, refetch } = useCsvData(endpoint, fileKey, page, pageSize, columnsHint, enabled);

  const columns = data?.columns ?? columnsHint ?? [];
  const rows = data?.rows ?? [];
  const hasMore = data?.hasMore ?? null;

  // First page load (no cached data yet) vs. background page changes.
  const showInitialSpinner = isLoading && rows.length === 0;

  const rangeStart = rows.length === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = page * pageSize + rows.length;
  // Prefer the endpoint's `truncated` flag; when unknown, treat a short page as the last.
  const isLastPage = hasMore === null ? rows.length < pageSize : !hasMore;

  const rangeLabel = useMemo(() => {
    if (showInitialSpinner) return "Loading…";
    if (rows.length === 0) return "No rows";
    return `${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()}${!isLastPage ? "+" : ""}`;
  }, [showInitialSpinner, rows.length, rangeStart, rangeEnd, isLastPage]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      {showInitialSpinner ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant='body2' color='error' sx={{ mb: 2 }}>
            Could not load table data.
          </Typography>
          <Button size='small' variant='outlined' onClick={() => refetch()}>
            Retry
          </Button>
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant='body2' color='text.secondary'>
            No rows to display.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight }}>
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} hover>
                  {columns.map((col) => (
                    <TableCell key={col} sx={{ whiteSpace: "nowrap", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {renderCell(row[col])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack direction='row' alignItems='center' spacing={2} sx={{ px: 3, py: 1.5 }}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Typography variant='caption' color='text.secondary'>
            Rows per page
          </Typography>
          <Select
            size='small'
            value={pageSize}
            onChange={(e) => {
              setPage(0);
              setPageSize(Number(e.target.value));
            }}
            sx={{ height: 32 }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Box sx={{ flex: 1 }} />

        {isFetching && !showInitialSpinner && <CircularProgress size={16} />}
        <Typography variant='caption' color='text.secondary'>
          {rangeLabel}
        </Typography>
        <IconButton size='small' onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || showInitialSpinner}>
          <NavigateBefore fontSize='small' />
        </IconButton>
        <IconButton size='small' onClick={() => setPage((p) => p + 1)} disabled={isLastPage || showInitialSpinner}>
          <NavigateNext fontSize='small' />
        </IconButton>
      </Stack>
    </Box>
  );
}
